/**
 * Единый визуальный язык списочных экранов (Афиша, Сообщества, Эксперты).
 * Новые списочные экраны собираются из этих компонентов.
 */
import type { ReactNode } from "react";
import { Search, SlidersHorizontal, MapPin, Check, X } from "lucide-react";
import type { FilterOption } from "@/lib/filter-options";
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

/* ---------- PageHeader ---------- */
export function PageHeader({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="max-h-[72px] min-w-0">
      <div className="flex min-w-0 items-center justify-between gap-3">
        <h1 className="min-w-0 truncate font-serif text-[28px] leading-10 text-td-dark">{title}</h1>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      {subtitle && (
        <p className="truncate font-sans text-[13px] leading-5 text-td-muted">{subtitle}</p>
      )}
    </header>
  );
}

/* ---------- FilterSheet ---------- */
export type FilterSectionDef =
  | {
      key: string;
      title: string;
      mode: "single";
      options: FilterOption[];
      value: string;
      onChange: (v: string) => void;
    }
  | {
      key: string;
      title: string;
      mode: "multi";
      options: FilterOption[];
      value: string[];
      onChange: (v: string[]) => void;
    };

export function FilterSections({ sections }: { sections: FilterSectionDef[] }) {
  return (
    <>
      {sections.map((s) => (
        <div key={s.key}>
          <h3 className="mb-2 font-sans text-[15px] font-semibold text-td-dark">{s.title}</h3>
          <div className="flex flex-wrap gap-2">
            {s.options.map((o) => {
              const active = s.mode === "single" ? s.value === o.value : s.value.includes(o.value);
              return (
                <button
                  key={o.value || "__all"}
                  type="button"
                  aria-pressed={active}
                  className={chipCls(active, !!o.muted)}
                  onClick={() => {
                    if (s.mode === "single") s.onChange(o.value);
                    else
                      s.onChange(
                        active ? s.value.filter((v) => v !== o.value) : [...s.value, o.value],
                      );
                  }}
                >
                  {s.mode === "multi" && active && <Check className="h-3.5 w-3.5" />}
                  {o.label}
                  {o.count !== undefined && (
                    <span className={active ? "opacity-80" : "text-td-muted"}>{o.count}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </>
  );
}

/** Общая шторка «Фильтры»: секции + произвольные блоки, «Сбросить» и «Применить» внизу. */
export function FilterSheet({
  open,
  onOpenChange,
  title = "Фильтры",
  sections = [],
  children,
  onReset,
  onApply,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  sections?: FilterSectionDef[];
  children?: ReactNode;
  onReset?: () => void;
  onApply?: () => void;
}) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" className="flex max-h-[85vh] flex-col gap-0 rounded-t-2xl p-0">
        <div className="min-h-0 flex-1 overflow-y-auto p-6">
        <SheetHeader className="text-left">
          <SheetTitle className="font-sans text-[17px] font-semibold text-td-dark">
            {title}
          </SheetTitle>
        </SheetHeader>
        <div className="mt-4 space-y-6">
          <FilterSections sections={sections} />
          {children}
        </div>
        </div>
        {(onReset || onApply) && (
          <SheetFooter className="flex-row gap-2 border-t border-td-line bg-white px-6 pt-3 pb-[calc(env(safe-area-inset-bottom)+12px)]">
            {onReset && (
              <Button variant="outline" className="h-11 flex-1 rounded-full" onClick={onReset}>
                Сбросить
              </Button>
            )}
            {onApply && (
              <Button
                className="h-11 flex-1 rounded-full bg-td-teal text-white hover:bg-td-teal/90"
                onClick={onApply}
              >
                Применить
              </Button>
            )}
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}

/* ---------- SearchFilterRow ---------- */
export function SearchFilterRow({
  value,
  onChange,
  placeholder = "Поиск",
  filterCount = 0,
  filtersTitle = "Фильтры",
  filtersOpen,
  onFiltersOpenChange,
  sections,
  filters,
  onReset,
  onApply,
  trailing,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  filterCount?: number;
  filtersTitle?: string;
  filtersOpen: boolean;
  onFiltersOpenChange: (open: boolean) => void;
  sections?: FilterSectionDef[];
  filters?: ReactNode;
  onReset?: () => void;
  onApply?: () => void;
  /** Иконка-кнопка справа от «Фильтры» (например, закладки). */
  trailing?: ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-full border border-td-line bg-white px-4 focus-within:border-td-teal">
        <Search className="h-4 w-4 shrink-0 text-td-muted" aria-hidden />
        <input
          type="search"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="min-w-0 flex-1 bg-transparent text-sm text-td-dark outline-none placeholder:text-td-muted"
        />
      </label>
      <button
        type="button"
        onClick={() => onFiltersOpenChange(true)}
        className={`inline-flex h-11 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition ${
          filterCount > 0
            ? "border-td-dark bg-td-dark text-white"
            : "border-td-line bg-white text-td-dark hover:border-td-teal"
        }`}
      >
        <SlidersHorizontal className="h-4 w-4" aria-hidden />
        <span>Фильтры</span>
        {filterCount > 0 && <span>{filterCount}</span>}
      </button>
      {trailing}
      <FilterSheet
        open={filtersOpen}
        onOpenChange={onFiltersOpenChange}
        title={filtersTitle}
        sections={sections}
        onReset={onReset}
        onApply={onApply}
      >
        {filters}
      </FilterSheet>
    </div>
  );
}

/** Круглая иконка-кнопка 44 px в строке поиска. */
export function IconToggleButton({
  active,
  onClick,
  label,
  children,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-pressed={active}
      onClick={onClick}
      className={`inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border transition ${
        active
          ? "border-td-dark bg-td-dark text-white"
          : "border-td-line bg-white text-td-dark hover:border-td-teal"
      }`}
    >
      {children}
    </button>
  );
}

/* ---------- ActiveFilters ---------- */
export function ActiveFilters({
  items,
  onReset,
}: {
  items: { key: string; label: string; onRemove: () => void }[];
  onReset: () => void;
}) {
  if (!items.length) return null;
  return (
    <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
      {items.map((i) => (
        <span
          key={i.key}
          className="inline-flex h-8 min-w-0 max-w-full items-center gap-1 rounded-full bg-td-soft pl-3 pr-1 text-[13px] text-td-dark"
        >
          <span className="min-w-0 truncate">{i.label}</span>
          <button
            type="button"
            aria-label={`Убрать «${i.label}»`}
            onClick={i.onRemove}
            className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-td-muted hover:bg-white hover:text-td-dark"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </span>
      ))}
      <button
        type="button"
        onClick={onReset}
        className="inline-flex h-8 items-center px-2 text-[13px] font-medium text-td-teal hover:underline"
      >
        Сбросить
      </button>
    </div>
  );
}

/* ---------- SegmentedTabs ---------- */
export function SegmentedTabs<K extends string>({
  items,
  value,
  onChange,
}: {
  items: { key: K; label: string; badge?: number; icon?: ReactNode; ariaLabel?: string }[];
  value: K;
  onChange: (k: K) => void;
}) {
  return (
    <div className="relative min-w-0">
      <div
        role="tablist"
        className="flex min-w-0 gap-0.5 overflow-x-auto overscroll-x-contain rounded-full border border-td-line bg-td-soft p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((t) => {
          const active = t.key === value;
          return (
            <button
              key={t.key}
              role="tab"
              aria-selected={active}
              aria-label={t.ariaLabel}
              type="button"
              onClick={() => onChange(t.key)}
              className={`flex h-9 shrink-0 ${t.label ? "flex-auto" : "flex-none"} items-center justify-center gap-1 whitespace-nowrap rounded-full px-2.5 text-[13px] transition ${
                active
                  ? "bg-td-dark font-semibold text-white"
                  : "font-medium text-td-muted hover:text-td-dark"
              }`}
            >
              {t.icon}
              {t.label}
              {!!t.badge && t.badge > 0 && (
                <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-td-red px-1 text-[11px] font-bold text-white">
                  {t.badge > 9 ? "9+" : t.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ---------- ChipRow ---------- */
export const chipCls = (active: boolean, muted = false) =>
  `inline-flex h-9 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-3.5 text-[13px] font-medium transition ${
    active
      ? "border-td-dark bg-td-dark text-white"
      : `border-td-line bg-white ${muted ? "text-td-muted" : "text-td-dark"} hover:border-td-teal`
  }`;

export function ChipRow({
  items,
  value,
  onChange,
  allLabel = "Все",
  allCount,
}: {
  items: { key: string; label: string; count?: number }[];
  value: string | null;
  onChange: (k: string | null) => void;
  allLabel?: string;
  allCount?: number;
}) {
  return (
    <div className="flex w-full min-w-0 gap-2 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <button type="button" className={chipCls(value === null)} onClick={() => onChange(null)}>
        {allLabel}
        {allCount !== undefined && (
          <span className={value === null ? "opacity-80" : "text-td-muted"}>{allCount}</span>
        )}
      </button>
      {items.map((i) => {
        const active = value === i.key;
        return (
          <button
            key={i.key}
            type="button"
            className={chipCls(active, i.count === 0)}
            onClick={() => onChange(active ? null : i.key)}
          >
            {i.label}
            {i.count !== undefined && (
              <span className={active ? "opacity-80" : "text-td-muted"}>{i.count}</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- SectionTitle ---------- */
export function SectionTitle({
  children,
  aside,
  className = "",
}: {
  children: ReactNode;
  aside?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`flex min-w-0 items-baseline justify-between gap-2 ${className}`}>
      <h2 className="min-w-0 font-sans text-[17px] font-semibold leading-6 text-td-dark">
        {children}
      </h2>
      {aside && <span className="shrink-0 text-[13px] text-td-muted">{aside}</span>}
    </div>
  );
}

/* ---------- SocialProof ---------- */
export type ProofAvatar = { photo_url: string | null; initial: string };

export function SocialProof({
  count,
  label,
  avatars = [],
}: {
  count: number;
  label: string;
  avatars?: ProofAvatar[];
}) {
  if (!count || count <= 0) return null;
  const shown = avatars.slice(0, 5);
  return (
    <div className="flex min-w-0 items-center gap-2">
      {shown.length > 0 && (
        <div className="flex shrink-0 -space-x-2">
          {shown.map((a, i) =>
            a.photo_url ? (
              <img
                key={i}
                src={a.photo_url}
                alt=""
                loading="lazy"
                decoding="async"
                className="h-6 w-6 rounded-full object-cover ring-2 ring-white"
              />
            ) : (
              <span
                key={i}
                className="flex h-6 w-6 items-center justify-center rounded-full bg-td-soft text-[10px] font-semibold text-td-dark ring-2 ring-white"
              >
                {a.initial}
              </span>
            ),
          )}
        </div>
      )}
      <span className="min-w-0 truncate text-[13px] text-td-muted">{label}</span>
    </div>
  );
}

/* ---------- EntityCard ---------- */
export function EntityCard({
  image,
  meta,
  title,
  author,
  place,
  extra,
  proof,
  action,
  corner,
  onOpen,
  dimmed,
  children,
  top,
  bottom,
  highlight,
  id,
  ariaLabel,
}: {
  image: ReactNode;
  meta?: ReactNode;
  title: string;
  author?: ReactNode;
  place?: ReactNode;
  /** Строка 4 без пина (число участников, языки и т.п.). */
  extra?: ReactNode;
  proof?: ReactNode;
  action?: ReactNode;
  corner?: ReactNode;
  onOpen?: () => void;
  dimmed?: boolean;
  /** Доп. строки под основными (роли, плашки). */
  children?: ReactNode;
  top?: ReactNode;
  bottom?: ReactNode;
  highlight?: boolean;
  id?: string;
  ariaLabel?: string;
}) {
  return (
    <div
      id={id}
      aria-label={ariaLabel}
      role={onOpen ? "button" : undefined}
      tabIndex={onOpen ? 0 : undefined}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (onOpen && (e.key === "Enter" || e.key === " ") && e.target === e.currentTarget) {
          e.preventDefault();
          onOpen();
        }
      }}
      className={`relative min-w-0 cursor-pointer rounded-2xl border p-3 text-left transition hover:border-td-teal/60 ${highlight ? "border-td-teal bg-td-teal/5" : "border-td-line bg-white"}`}
    >
      {corner && (
        <div className="absolute right-2 top-2" onClick={(e) => e.stopPropagation()}>
          {corner}
        </div>
      )}
      <div className="flex min-w-0 gap-3">
        <div
          className={`h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-td-soft ${dimmed ? "opacity-60 saturate-50" : ""}`}
        >
          {image}
        </div>
        <div className={`min-w-0 flex-1 ${corner ? "pr-8" : ""}`}>
          {meta && (
            <div className="flex min-w-0 flex-wrap items-center gap-1.5 text-[13px] font-medium text-td-teal">
              {meta}
            </div>
          )}
          <h3 className="mt-1 line-clamp-2 font-sans break-words text-base font-semibold leading-snug text-td-dark [overflow-wrap:anywhere]">
            {title}
          </h3>
          {author && <div className="mt-1 min-w-0 text-[13px] text-td-muted">{author}</div>}
          {place && (
            <div className="mt-1 flex min-w-0 items-center gap-1 text-[13px] text-td-muted">
              <MapPin className="h-3.5 w-3.5 shrink-0" aria-hidden />
              <span className="min-w-0 truncate">{place}</span>
            </div>
          )}
          {extra && <div className="mt-1 min-w-0 truncate text-[13px] text-td-muted">{extra}</div>}
          {children}
        </div>
      </div>
      {(proof || action) && (
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">{proof}</div>
          {action && (
            <div
              className="ml-auto flex min-w-0 max-w-full items-center gap-1.5"
              onClick={(e) => e.stopPropagation()}
            >
              {action}
            </div>
          )}
        </div>
      )}
      {bottom}
    </div>
  );
}

/* ---------- Мелкие плашки ---------- */
export function NeutralPill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full bg-td-soft px-2 py-0.5 text-[11px] font-semibold text-td-dark">
      {children}
    </span>
  );
}
export function OutlinePill({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex min-w-0 max-w-full shrink items-center gap-1 truncate rounded-full border border-td-teal px-2 py-0.5 text-[11px] font-semibold text-td-teal">
      {children}
    </span>
  );
}
